import { useCallback, useSyncExternalStore } from 'react'

import { getItem, setItem } from '../storage/scopedStorage'

/**
 * Which sales-order-create screen a salesperson sees (T-035 — the merge of
 * the old screen and the Sprint 3 counter into one, "new by default" screen).
 *
 * ── WHY THIS IS A DIFFERENT MECHANISM FROM src/features/flags.js ────────────
 *
 * flags.js's FLAGS.NEW_COUNTER was a THREE-WEEK ROLLBACK SWITCH: the counter
 * was unproven, so the default stayed off and an env var flipped it for
 * everyone at once. That job is finished — the merged screen below is now the
 * one every new user sees — so this is a PERMANENT, PER-PERSON PREFERENCE
 * instead: "which of the two screens do I want", answered once and remembered.
 *
 * It is deliberately a separate key rather than a repurposed flag: a flag that
 * used to mean "the new thing is risky, default it off" would be a strange
 * name for "the new thing is now the default, but you can still opt out".
 *
 * ── WHERE THIS IS STORED, HONESTLY ───────────────────────────────────────────
 *
 * Through the shop-and-user-scoped store (src/storage/scopedStorage.js), which
 * is localStorage underneath. That means:
 *
 *   - it survives a reload and a new tab, for the SAME browser profile;
 *   - two salespeople sharing one counter PC each keep their own choice,
 *     because the key is scoped by user (and by shop);
 *   - it does NOT follow a user to a different computer or browser — there is
 *     no server-side account setting behind this, only a browser preference.
 *     Do not oversell it as synced; it is not.
 */

const STORAGE_KEY = 'salesOrderUiVersion'

export const UI_VERSIONS = {
    NEW: 'new',
    LEGACY: 'legacy',
}

/**
 * No stored preference yet → the new, merged screen. This is the app's
 * current default, superseding the old build-time REACT_APP_FEATURE_NEW_COUNTER
 * default of "off".
 */
const DEFAULT_VERSION = UI_VERSIONS.NEW

const listeners = new Set()

function announce() {
    listeners.forEach((fn) => fn())
}

function subscribe(fn) {
    listeners.add(fn)
    // A second tab open on the same counter PC follows a switch made in the
    // first one, the same reasoning useFeature.js gives for the old flag.
    window.addEventListener('storage', fn)
    return () => {
        listeners.delete(fn)
        window.removeEventListener('storage', fn)
    }
}

function readStored() {
    const stored = getItem(STORAGE_KEY, null)
    return stored === UI_VERSIONS.NEW || stored === UI_VERSIONS.LEGACY ? stored : DEFAULT_VERSION
}

/** For call sites outside React (routing guards that do not want the hook). */
export function getInvoiceUiVersion() {
    return readStored()
}

/**
 * Records a deliberate choice. Persists immediately and announces it to every
 * subscriber in this tab.
 * @param {string} version one of UI_VERSIONS
 */
export function setInvoiceUiVersion(version) {
    const next = version === UI_VERSIONS.LEGACY ? UI_VERSIONS.LEGACY : UI_VERSIONS.NEW
    setItem(STORAGE_KEY, next)
    announce()
    return next
}

/**
 * @returns {[string, (version: string) => void]} the current version, and a setter.
 */
export function useInvoiceUiVersion() {
    const version = useSyncExternalStore(subscribe, readStored, readStored)
    const setVersion = useCallback((next) => setInvoiceUiVersion(next), [])
    return [version, setVersion]
}

export default useInvoiceUiVersion
